import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import {
  format,
  addWeeks,
  startOfWeek,
  addDays,
  getDate,
  subWeeks,
} from "date-fns";
import { nl } from 'date-fns/locale';

import { useNavigate } from "react-router-dom";
import {
  getFontStatusColor,
  getViewStatusColor,
  isWeekday,
} from "../utils/commonMethods";
import { AxiosResponse, BaseUrl } from "../utils/urlConst";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import apiService from "../apiService/ApiService";
import BundleTaskItem, {
  Event, getTextContent,
} from "../components/TaskPlanning/BundleTaskItem";
import CommonLoader from "../components/CommonLoader";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { useAlert } from "../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import PlanningHeader from "../components/TaskPlanning/PlanningHeader";
import useResponsive from "../hooks/useResponsive";
import { useDrawer } from "../contexts/DrawerContext";
import { closedDrawerWidth, drawerWidth } from "../layout/CommonDrawer";
import PMRemarkDialog from "../components/TaskDetails/PMRemarkDialog";
import { useTimerContext } from "../contexts/TimerContext";
import { useProjectContext } from "../contexts/ProjectContext";

interface WeekDetails {
  formatted: string;
  header: string;
  date: Date;
  day: number;
}
interface BoxDimensions {
  width: number;
  height: number;
}
const TaskPlanning: React.FC = () => {
  const [dimensions, setDimensions] = useState<BoxDimensions>({
    width: 0,
    height: 0,
  });
  let columnCount = 7;
  const { isMobile, isTablet } = useResponsive();

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [isLoading, setIsLoading] = useState(true);
  const [isShowDetailPopup, setIsShowDetailPopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Event | null>(null);
  const [week, setWeek] = useState<WeekDetails[]>([]);
  const [weekCount, setWeekCount] = useState("");
  const [weekStartDate, setWeekStartDate] = useState("");
  const [weekEndDate, setWeekEndDate] = useState("");
  const [month, setMonth] = useState("");
  const [planDetails, setPlanDetails] = useState([]);
  const [date, setDate] = useState(new Date());
  const { isDrawerOpen } = useDrawer();
  const { windowWidth, windowHeight } = useWindowDimensions()

  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const {
    setSelectedProjectIdForNavigation
  } = useTimerContext();
  const {
    setSelectedProjectId,
    setSelectedProjectName,
    setSelectedProjectItem,
  } = useProjectContext();


  // goto previous week function
  const handlePreviousWeek = () => {
    setDate(subWeeks(date, 1));
  };

  // goto next week function
  const handleNextWeek = () => {
    setDate(addWeeks(date, 1));
  };

  // Day pick from the calendar
  const onDayPress = async (selectedDay: Date) => {
    // Check if the selected day is a weekday (Monday to Friday)
    if (!isWeekday(selectedDay)) {
      return;
    }
    // Update the current month of the calendar
    setDate(selectedDay);
    // Calculate the start of the selected week
    const selectedWeekStart = startOfWeek(selectedDay, {
      weekStartsOn: 1,
    });
    // Generate marked dates for the entire week
    const updatedMarkedDates: any = {};
    for (let i = 0; i < 7; i++) {
      const currentDay = addDays(selectedWeekStart, i);
      const currentDateString = format(currentDay, "yyyy-MM-dd");
      updatedMarkedDates[currentDateString] = {
        selected: true,
        marked: true,
        selectedColor: "green",
      };
    }
    const updatedWeek = getWeekDays(selectedWeekStart);
    setWeek(updatedWeek);
  };

  // Week days fetching
  const getWeekDays = (startDate: Date) => {
    const start = startOfWeek(startDate, { weekStartsOn: 1 });
    const weekDetails: WeekDetails[] = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = addDays(start, i);
      const formattedHeader = format(currentDate, "E.dd-MM-yyyy");
      weekDetails.push({
        formatted: t(`days.${daysOfWeek[i]}`),
        header: formattedHeader,
        date: currentDate,
        day: getDate(currentDate),
      });
    }
    return weekDetails;
  };

  // Get week info, Month, Weekcount in Calendar header part
  const getWeekInfo = async (date: Date) => {
    const weekCount = format(startOfWeek(date, { weekStartsOn: 1 }), "w");
    const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
    const endOfWeekDate = addDays(startOfWeekDate, 6);
    const startOfWeekFormatted = format(startOfWeekDate, "d");
    const endOfWeekFormatted = format(endOfWeekDate, "d");
    // Get the translated start and end days using your translation function or library
    const startOfWeekDayTranslationKey = format(startOfWeekDate, "EEE");
    const endOfWeekDayTranslationKey = format(endOfWeekDate, "EEE");

    const translatedStartOfWeekDay = t(
      `weekDays.${startOfWeekDayTranslationKey}`
    );
    const translatedEndOfWeekDay = t(`weekDays.${endOfWeekDayTranslationKey}`);

    const monthTranslationKey = format(endOfWeekDate, "MMMM");
    setMonth(t(`months.${monthTranslationKey}`));
    setWeekCount(weekCount);
    setWeekStartDate(`${translatedStartOfWeekDay}.${startOfWeekFormatted}`);
    setWeekEndDate(`${translatedEndOfWeekDay}.${endOfWeekFormatted}`);
  };
  // Fetch week days based on the current date
  const fetchData = useCallback(() => {
    const weekDays = getWeekDays(date);
    setWeek(weekDays);
    getWeekInfo(date);
  }, [date]);

  // Fetch Planning Details API, params - statDate, endDate and user_id
  const fetchPlanningDetails = useCallback(
    async (startDate: string, endDate: string) => {
      setIsLoading(true);
      setPlanDetails([]);
      let workerId = LocalStorageUtil.getItem(storageKeys.workerId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
      let isFlexRole = LocalStorageUtil.getItem(storageKeys.isFlexRole, null);
      const isFlexible = isFlexRole === 1 ? "true" : "false";

      apiService.setAuthToken(userToken);
      // Make an authenticated request
      const responseJson: AxiosResponse | null = await apiService.get(
        `${BaseUrl}/planningWorkersListing?page=1&weekStartDate=${startDate}&weekEndDate=${endDate}&sortOrder=asc&sortField=medewerker_name&workerFilter=${workerId}&apiType=field_app&isFlexible=${isFlexRole}`
      );
      if (
        responseJson &&
        (responseJson.status === true || responseJson.status_code === 1)
      ) {
        let data;
        if (isFlexible === "true") {
          data = responseJson.data.flexible && responseJson.data.flexible[workerId];
        } else {
          data = responseJson.data.medewerker && responseJson.data.medewerker[workerId];
        }
        setPlanDetails(data);
      } else {
        showAlert(t("common.server_error"), "error");
      }
      setIsLoading(false);
    },
    []
  );

  const getWindowWidth = () => {
    if (isMobile || isTablet) {
      return windowWidth - 4
    } else {
      if (isDrawerOpen === true) {
        return (windowWidth - drawerWidth - 4)
      } else {
        return (windowWidth - closedDrawerWidth - 4)
      }
    }
  }

  useEffect(() => {
    const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
    const endOfWeekDate = addDays(startOfWeekDate, 6);
    // Use the start and end dates as needed
    const startDate = format(startOfWeekDate, "yyyy-MM-dd");
    const endDate = format(endOfWeekDate, "yyyy-MM-dd");

    fetchData();
    // initialCalendarWeekSelection();
    // Planning details Api call
    fetchPlanningDetails(startDate, endDate);
  }, [date, t]);

  useEffect(() => {
    // Function to update dimensions
    setDimensions({
      width: getWindowWidth(),
      height: windowHeight,
    });

  }, [isDrawerOpen]);


  const getPlanningItemWidth = (event: Event) => {
    return event.card_length > 0
      ? (dimensions.width / columnCount) * event.card_length
      : dimensions.width / columnCount;
  };
  const getPlanningItemLeftMargin = (event: Event) => {
    return event.left_space_count !== 0
      ? (event.left_space_count * (dimensions.width / columnCount)) / 8
      : 0;
  };
  const handleClosePMRemarkDialog = () => {
    setIsShowDetailPopup(false);
  };
  useEffect(() => {
    setSelectedProjectIdForNavigation(null)
  }, [])

  const renderPlanningLists = (eventGroup: Event[]) => {
    return eventGroup.map((event, eventIndex) => (
      <Box
        key={eventIndex}
        sx={{
          width: getPlanningItemWidth(event),
          marginLeft: getPlanningItemLeftMargin(event),
        }}
      >
        {event.remarks_status === 1 || event.remarks_status === null ? (
          !event.bundle_status ? (
            <ListItemButton
              onClick={() => {
                if (!event?.is_static) {
                  if (event.medewerker_level === 0) {
                    setSelectedProjectIdForNavigation(event?.project_id)
                    navigate(`/taskdetail/${event.task_id}`);
                  } else {
                    setSelectedProjectIdForNavigation(event?.project_id)
                    setSelectedProjectId(event?.project_id)
                    setSelectedProjectName(event?.project_name)
                    setSelectedProjectItem({ id: event?.project_id, name: event?.project_name })
                    navigate(`/task`);
                  }
                }
              }}
              sx={{
                padding: 0,
                ...(event.is_static && {
                  pointerEvents: "none",
                  opacity: 1,
                  color: "inherit",
                }),
              }}
            >
              <BundleTaskItem event={event} isStatic={event.is_static === 1} />
            </ListItemButton>
          ) : (
            (isMobile) ?
              <ListItemButton
                onClick={() => {
                  setIsShowDetailPopup(true);
                  setSelectedItem(event)
                }}
                sx={{
                  padding: 0,
                  ...(event.is_static && {
                    pointerEvents: "none",
                    opacity: 1,
                    color: "inherit",
                  }),
                }}
              >
                <BundleTaskItem event={event} isStatic={event.is_static === 1} />
              </ListItemButton> :
              <BundleTaskItem event={event} isStatic={event.is_static === 1} />
          )
        ) : (
          (isMobile ? (
            <ListItemButton
              onClick={() => {
                setIsShowDetailPopup(true);
                setSelectedItem(event)
              }}
              sx={{
                padding: 0,
                ...(event.is_static && {
                  pointerEvents: "none",
                  opacity: 1,
                  color: "inherit",
                }),
              }}
            >
              <Box
                sx={{
                  backgroundColor: getViewStatusColor(event.remarks_status),
                  borderRadius: "4px",
                  display: "flex",
                  flex: 1,
                  padding: 0.5,
                  marginX: 0.25,
                  height: "32px",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: getFontStatusColor(event.remarks_status),
                    fontSize: "10px",
                    fontFamily: "Nunito Sans SemiBold",
                    textOverflow: "ellipsis",
                  }}
                  noWrap
                >
                  {(event.from_time &&
                    event.to_time) ? event.from_time + " - " + event.to_time + " " + event.remarks
                    : event.remarks}
                </Typography>
              </Box>
            </ListItemButton>
          ) : (

            <Box
              sx={{
                backgroundColor: getViewStatusColor(event.remarks_status),
                borderRadius: "2px",
                padding: 0.5,
                marginX: 0.25
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                }}
              >
                {event.from_time &&
                  event.to_time &&
                  <Typography
                    sx={{
                      color: getFontStatusColor(event.remarks_status),
                      fontFamily: "Nunito Sans Regular700",
                      fontSize: "14px",
                      marginRight: 1,
                    }}
                  >
                    {event.from_time} - {event.to_time}
                  </Typography>}
                <Typography
                  sx={{
                    color: getFontStatusColor(event.remarks_status),
                    fontFamily: "Nunito Sans Regular500",
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {" " + event.remarks}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        display: "flex", flexDirection: "column", overflow: "hidden",
        mt: "3px", mb: 5
      }}
    >
      <Box
        component="main"
        sx={{ flexGrow: 1 }}
      >
        <CommonLoader isVisible={isLoading} />
        <PlanningHeader
          handlePreviousWeek={handlePreviousWeek}
          currentWeek={weekCount}
          handleNextWeek={handleNextWeek}
          weekStartDate={weekStartDate}
          weekEndDate={weekEndDate}
          onDayPress={onDayPress}
          month={month}
          selectedDate={date}
        />

        <Box sx={{ flex: 1, mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: 35,
            }}
          >
            {week.map((weekDay) => (
              <Box
                key={weekDay.formatted}
                sx={{
                  flex: 1,
                  padding: theme.spacing(1),
                  borderRight: `1px solid ${theme.palette.secondary.main}`,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: theme.palette.blueColors.main,
                }}
              >
                {isMobile ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito Sans Regular700",
                        fontSize: "10px",
                      }}
                      color={theme.palette.secondary.main}
                    >
                      {weekDay.formatted + "."}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Nunito Sans Regular700",
                        fontSize: "10px",
                      }}
                      color={theme.palette.secondary.main}
                    >
                      {format(weekDay.date, "MMM dd", { locale: nl })}
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans Regular700",
                      fontSize: "14px",
                    }}
                    color={theme.palette.secondary.main}
                  >
                    {weekDay.formatted}.{format(weekDay.date, "dd-MM-yyyy")}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
            }}
          >
            {(!planDetails || planDetails.length === 0) && !isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  height: 250,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Nunito Sans Regular500",
                    fontSize: "14px",
                    color: theme.palette.blueColors.main
                  }}
                >
                  {t("taskOverViewScreen.noPendingTasks")}
                </Typography>
              </Box>
            ) : (
              <List>
                {planDetails?.map((eventGroup, groupIndex) => (
                  <ListItem
                    key={groupIndex}
                    sx={{ display: "flex", paddingX: "2px", paddingY: "3px" }}
                  >
                    {renderPlanningLists(eventGroup)}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Box>
      <PMRemarkDialog
        open={isShowDetailPopup}
        onClose={handleClosePMRemarkDialog}
        remarkText={
          selectedItem
            ? (selectedItem.remarks_status === 1 || selectedItem.remarks_status === null
              ? getTextContent(selectedItem, selectedItem.is_static === 1)
              : (selectedItem.from_time && selectedItem.to_time
                ? `${selectedItem.from_time} - ${selectedItem.to_time} ${selectedItem.remarks ?? ''}`
                : selectedItem.remarks ?? ''
              )
            )
            : null
        }
      />
    </Box>
  );
};

export default TaskPlanning;
